import { useEffect, useRef } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { Logger } from '@utils/logger';
import {
    getRiskIqProtocolCommentCountsById as getCommentCountsByIdReturnType,
    getRiskIqProtocolCommentCountsByIdVariables,
    getRiskIqProtocolCommentCounts as getCommentCountsReturnType,
    getRiskIqProtocolCommentCountsVariables,
} from '@queries/generated/risk-assessment.generated';
import {
    GET_RISK_IQ_PROTOCOL_COMMENT_COUNTS,
    GET_RISK_IQ_PROTOCOL_COMMENT_COUNTS_BY_ID,
} from '@queries/risk-assessment';

const useCommentCountQueries = (studyId: string | undefined) => {
    const apolloClient = useApolloClient();

    // Each abort controller with its timeout, so the timeout can be cleared once the query has finished
    const abortControllersRef = useRef<Map<AbortController, NodeJS.Timeout>>(new Map());

    const getAbortController = () => {
        const abortController = new AbortController();

        const timeout = setTimeout(() => {
            abortController.abort('Timeout');
        }, 60000);

        abortControllersRef.current.set(abortController, timeout);

        return abortController;
    };

    const removeAbortController = (abortController: AbortController) => {
        clearTimeout(abortControllersRef.current.get(abortController));
        abortControllersRef.current.delete(abortController);
    };

    const getCommentCounts = async (protocolDocumentId: string) => {
        const abortController = getAbortController();
        try {
            const result = await apolloClient.query<
                getCommentCountsReturnType,
                getRiskIqProtocolCommentCountsVariables
            >({
                query: GET_RISK_IQ_PROTOCOL_COMMENT_COUNTS,
                variables: {
                    commentsInput: {
                        protocolDocumentId,
                        studyId,
                    },
                },
                fetchPolicy: 'no-cache',
                context: {
                    fetchOptions: { signal: abortController.signal },
                },
            });

            if (result.data?.riskIqProtocolCommentCounts) {
                return result.data.riskIqProtocolCommentCounts;
            }
        } catch (error) {
            Logger.error('error when getting comment counts', error);
        } finally {
            removeAbortController(abortController);
        }

        return null;
    };

    const getCommentCountsById = async (riskIds: string[], ctqIds: string[]) => {
        const abortController = getAbortController();
        try {
            const result = await apolloClient.query<
                getCommentCountsByIdReturnType,
                getRiskIqProtocolCommentCountsByIdVariables
            >({
                query: GET_RISK_IQ_PROTOCOL_COMMENT_COUNTS_BY_ID,
                variables: {
                    commentsInput: {
                        riskIds,
                        ctqIds,
                        studyId,
                    },
                },
                fetchPolicy: 'no-cache',
                context: {
                    fetchOptions: { signal: abortController.signal },
                },
            });

            if (result.data?.riskIqProtocolCommentCountsById) {
                return result.data.riskIqProtocolCommentCountsById;
            }
        } catch (error) {
            Logger.error('error when getting comment counts by id', error);
        } finally {
            removeAbortController(abortController);
        }

        return null;
    };

    useEffect(() => {
        return () => {
            abortControllersRef.current.forEach((timeout, controller) => {
                clearTimeout(timeout);
                controller.abort('Unmount');
            });
        };
    }, []);

    return {
        getCommentCounts,
        getCommentCountsById,
    };
};

export default useCommentCountQueries;
